import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import HelpButton from "../components/HelpButton";
import QrSheetModal from "../components/QrSheet";
import PhotoCropper from "../components/PhotoCropper";
import ThemedBanner, { ORDER_CSS } from "../components/ThemedBanner";
import { THEMES, THEME_KEYS, getTheme, themeVars } from "../onlineThemes";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, card, buttonPrimary, buttonSecondary, inputBase, pageHeading } from "../theme";
import { getAuthHeader, getLoginRedirect, getBusinessName } from "../session";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const money = (n) => `$${Number(n).toFixed(2)}`;

// The restaurant's own online-ordering setup: link + QR, payment method, rules, hours, Stripe connection, recent orders.
export default function OnlineOrderingPage() {
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [off, setOff] = useState(false);
  const [msg, setMsg] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [look, setLook] = useState(null);
  const [lookForm, setLookForm] = useState({ theme: "generic", tagline: "" });
  const [crop, setCrop] = useState(null); // {kind, file}
  const [tab, setTab] = useState("orders");
  const [previewMode, setPreviewMode] = useState("phone");
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const API = endpoints.onlinePlace.replace(/\/online\/place\.php$/, "");
  const h = () => ({ headers: getAuthHeader() });

  const adopt = (d) => {
    setS(d);
    setF({
      payment_mode: d.payment_mode, store_limit: d.store_limit === null ? "" : d.store_limit, store_limit_none: d.store_limit === null, min_order: d.min_order,
      prep_minutes: d.prep_minutes, hold_minutes: d.hold_minutes, slot_max_orders: d.slot_max_orders, max_days_ahead: d.max_days_ahead,
      asap_enabled: d.asap_enabled, scheduled_enabled: d.scheduled_enabled, pickup_notes: d.pickup_notes || "", hours: d.hours,
    });
  };
  const loadHistory = (query = "", page = 1) => {
    axios.get(endpoints.onlineOrdersHistory(query, page, historyPageSize), h())
      .then((res) => { 
        setHistory(res.data.orders);
        setHistoryTotalCount(res.data.total_count || 0);
        setHistoryTotalPages(res.data.total_pages || 1);
        setHistoryPage(res.data.page || 1);
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (!getAuthHeader().Authorization) { window.location.href = getLoginRedirect(); return; }
    setBusinessName(getBusinessName());
    axios.get(endpoints.onlineSettings, h())
      .then((res) => {
        adopt(res.data);
        if (/stripe=(return|refresh)/.test(window.location.search)) {
          axios.post(endpoints.onlineSettings, { action: "stripe_refresh" }, h()).then((r) => adopt(r.data)).catch(() => {});
        }
      })
      .catch((err) => { if (err.response?.data?.code === "module_disabled") setOff(true); else if ([401, 403].includes(err.response?.status)) window.location.href = getLoginRedirect(); });
    axios.get(endpoints.onlineBranding, h()).then((res) => { setLook(res.data); setLookForm({ theme: res.data.theme, tagline: res.data.tagline || "" }); }).catch(() => {});
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fail = (err, fallback) => setMsg({ ok: false, text: err.response?.data?.error || fallback });
  const save = async () => {
    setMsg(null);
    const body = {
      payment_mode: f.payment_mode, min_order: Number(f.min_order), prep_minutes: Number(f.prep_minutes), hold_minutes: Number(f.hold_minutes),
      slot_max_orders: Number(f.slot_max_orders), max_days_ahead: Number(f.max_days_ahead), asap_enabled: f.asap_enabled, scheduled_enabled: f.scheduled_enabled,
      pickup_notes: f.pickup_notes, hours: f.hours,
    };
    if (f.store_limit_none) body.store_limit_none = true; else body.store_limit = Number(f.store_limit);
    try { const res = await axios.patch(endpoints.onlineSettings, body, h()); adopt(res.data); setMsg({ ok: true, text: "Saved." }); } catch (err) { fail(err, "Couldn't save."); }
  };
  const toggleAccepting = async () => {
    setMsg(null);
    try { const res = await axios.patch(endpoints.onlineSettings, { accepting: !s.accepting }, h()); adopt(res.data); } catch (err) { fail(err, "Couldn't change that."); }
  };
  const stripeAction = async (action) => {
    setMsg(null);
    try {
      const res = await axios.post(endpoints.onlineSettings, { action }, h());
      adopt(res.data);
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { fail(err, "Stripe didn't respond."); }
  };
  const saveLook = async () => {
    setMsg(null);
    try { const res = await axios.patch(endpoints.onlineBranding, { theme: lookForm.theme, tagline: lookForm.tagline }, h()); setLook(res.data); setMsg({ ok: true, text: "Look and feel saved." }); } catch (err) { fail(err, "Couldn't save."); }
  };
  const uploadPicture = async (kind, photo) => {
    setMsg(null);
    try { const res = await axios.post(endpoints.onlineBranding, { kind, photo }, h()); setLook(res.data); setCrop(null); setMsg({ ok: true, text: kind === "banner" ? "Banner saved." : "Logo saved." }); } catch (err) { fail(err, "Couldn't upload the picture."); }
  };
  const removePicture = async (kind) => {
    try { const res = await axios.delete(endpoints.onlineBrandingDelete(kind), h()); setLook(res.data); } catch (err) { fail(err, "Couldn't remove it."); }
  };
  const setDay = (k, patch) => setF({ ...f, hours: { ...f.hours, [k]: { ...f.hours[k], ...patch } } });
  const copy = () => { navigator.clipboard?.writeText(s.order_url); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const shell = (children) => (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 1500, minWidth: 0 }}>{children}</div>
    </div>
  );
  if (off) return shell(<><h1 style={pageHeading}>Online Ordering</h1><div style={{ ...card, marginTop: space[4] }}><p style={{ fontFamily: fonts.body, color: colors.textSecondary, marginTop: 0 }}>Online ordering isn't turned on for your restaurant yet.</p><Link to="/settings" style={{ ...buttonPrimary, textDecoration: "none", display: "inline-block" }}>Request it under Settings</Link></div></>);
  if (!s || !f) return shell(<p style={{ fontFamily: fonts.body }}>Loading…</p>);

  const st = s.stripe;
  return shell(
    <>
      <h1 style={pageHeading}>Online Ordering <HelpButton page="onlineOrdering" /></h1>

      <div style={{ ...card, marginTop: space[3] }} data-testid="online-status">
        <div style={styles.row}>
          <div>
            <div style={styles.title}>{s.accepting ? "Accepting online orders" : "Not accepting online orders"}</div>
            <div style={styles.hint}>Turn this off any time, for example when the kitchen is overwhelmed.</div>
          </div>
          <button style={s.accepting ? { ...buttonSecondary, color: "#b42318" } : buttonPrimary} onClick={toggleAccepting} data-testid="accepting-toggle">{s.accepting ? "Stop accepting" : "Start accepting"}</button>
        </div>
        <div style={{ marginTop: space[3] }}>
          <div style={styles.hint}>Customers order and track here:</div>
          <div style={{ display: "flex", gap: space[2], flexWrap: "wrap", alignItems: "center" }}>
            <input readOnly value={s.order_url} style={{ ...inputBase, flex: "1 1 260px", minWidth: 0, fontSize: 12 }} onFocus={(e) => e.target.select()} aria-label="Ordering link" />
            <button style={buttonSecondary} onClick={copy}>{copied ? "Copied!" : "Copy"}</button>
            <button style={buttonSecondary} onClick={() => setShowQr(true)}>Print QR code</button>
          </div>
        </div>
      </div>

      {msg && <div role="status" style={{ ...styles.msg, background: msg.ok ? "#ecfdf3" : "#fef3f2", color: msg.ok ? "#067647" : "#b42318" }}>{msg.text}</div>}

      <div role="tablist" style={{ display: "flex", gap: 6, marginTop: space[3], flexWrap: "wrap" }} data-testid="online-tabs">
        {[["orders", "Orders"], ["look", "Look and feel"], ["pay", "Payments and rules"]].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={{ padding: "8px 16px", borderRadius: radius.md, border: `1px solid ${tab === k ? colors.brandBlue : colors.border}`, background: tab === k ? colors.brandBlue : "transparent", color: tab === k ? "#fff" : colors.textPrimary, fontFamily: fonts.body, fontSize: 14, cursor: "pointer" }}>{label}</button>
        ))}
      </div>
      <style>{`.oo-two{display:grid;gap:16px;grid-template-columns:minmax(0,1fr)}@media(min-width:1100px){.oo-two{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}}.oo-sticky{position:sticky;top:16px;align-self:start}.oo-hours{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(430px,100%),1fr));gap:2px 24px}@media(min-width:700px){.oo-hours>div{flex-wrap:nowrap!important}.oo-hours input[type=time]{width:110px!important}}`}</style>

      {tab === "look" && look && (
        <div className="oo-two" style={{ marginTop: space[3] }}>
        <div style={{ ...card, marginTop: 0 }} data-testid="look-and-feel">
          <div style={styles.title}>Look and feel</div>
          <p style={styles.hint}>Pick a theme and add your banner and logo. Customers see this on your online menu, checkout and order tracking.</p>
          <div style={styles.themeGrid}>
            {THEME_KEYS.map((k) => {
              const t = THEMES[k];
              return (
                <button key={k} type="button" aria-label={`Theme ${t.name}`} aria-pressed={lookForm.theme === k} onClick={() => setLookForm({ ...lookForm, theme: k })} style={{ ...styles.themeTile, background: t.bg, color: t.text, borderColor: lookForm.theme === k ? colors.brandBlue : colors.border, borderWidth: lookForm.theme === k ? 3 : 1 }}>
                  <span style={{ display: "block", height: 26, background: t.banner, borderRadius: 4, marginBottom: 6 }} />
                  <span style={{ fontFamily: t.head, fontWeight: t.headWeight, fontSize: 14, display: "block", textAlign: "left" }}>{t.name}</span>
                  <span style={{ display: "block", background: t.accent, color: t.onAccent, borderRadius: t.radius > 6 ? 8 : 3, fontSize: 11, padding: "3px 0", marginTop: 6, textAlign: "center" }}>Add to order</span>
                </button>
              );
            })}
          </div>
          <label style={{ ...styles.field, marginTop: space[3] }}>Tagline (optional)
            <input style={inputBase} maxLength={140} value={lookForm.tagline} onChange={(e) => setLookForm({ ...lookForm, tagline: e.target.value })} placeholder="e.g. Wood-fired tacos and fresh salsas" aria-label="Tagline" />
          </label>
          <div style={{ display: "flex", gap: space[5], flexWrap: "wrap", marginTop: space[3] }}>
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ fontFamily: fonts.body, fontSize: 13, fontWeight: 600 }}>Banner</div>
              <div style={styles.hint}>Fixed 3:1 shape (best: 1200 × 400 px), shown exactly as you crop it. You choose what part of your photo shows.</div>
              <label style={{ ...buttonSecondary, padding: "6px 12px", fontSize: 13, cursor: "pointer", display: "inline-block" }}>{look.banner_url ? "Change banner" : "Add banner photo"}<input type="file" accept="image/*" style={{ display: "none" }} aria-label="Banner photo" onChange={(e) => { if (e.target.files[0]) setCrop({ kind: "banner", file: e.target.files[0] }); e.target.value = ""; }} /></label>
              {look.banner_url && <button style={styles.removeLink} onClick={() => removePicture("banner")}>Remove</button>}
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <div style={{ fontFamily: fonts.body, fontSize: 13, fontWeight: 600 }}>Logo</div>
              <div style={styles.hint}>Square (best: 256 × 256 px). Without one, your initials are shown.</div>
              <label style={{ ...buttonSecondary, padding: "6px 12px", fontSize: 13, cursor: "pointer", display: "inline-block" }}>{look.logo_url ? "Change logo" : "Add logo"}<input type="file" accept="image/*" style={{ display: "none" }} aria-label="Logo picture" onChange={(e) => { if (e.target.files[0]) setCrop({ kind: "logo", file: e.target.files[0] }); e.target.value = ""; }} /></label>
              {look.logo_url && <button style={styles.removeLink} onClick={() => removePicture("logo")}>Remove</button>}
            </div>
          </div>
          {crop && <PhotoCropper file={crop.file} label={crop.kind === "banner" ? "Banner" : "Logo"} outW={crop.kind === "banner" ? 1200 : 256} outH={crop.kind === "banner" ? 400 : 256} maxChars={crop.kind === "banner" ? 390000 : 130000} onSave={(photo) => uploadPicture(crop.kind, photo)} onCancel={() => setCrop(null)} />}

          <div style={{ marginTop: space[3] }}><button style={buttonPrimary} onClick={saveLook} data-testid="save-look">Save look and feel</button></div>
        </div>
        <div className="oo-sticky" style={{ ...card, marginTop: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space[2] }}><div style={styles.title}>Live preview</div><div style={{ display: "flex", gap: 6 }}>{["phone", "desktop"].map((m) => <button key={m} onClick={() => setPreviewMode(m)} aria-pressed={previewMode === m} style={{ padding: "4px 12px", borderRadius: radius.sm, border: `1px solid ${previewMode === m ? colors.brandBlue : colors.border}`, background: previewMode === m ? colors.brandBlue : "transparent", color: previewMode === m ? "#fff" : colors.textPrimary, fontSize: 12, cursor: "pointer", textTransform: "capitalize" }}>{m}</button>)}</div></div>
          <div style={{ maxWidth: previewMode === "phone" ? 390 : "100%", margin: "0 auto", border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: "hidden" }} data-testid="look-preview">
            <div className="op-root" style={{ ...themeVars(getTheme(lookForm.theme)), minHeight: 0, paddingBottom: 16 }}>
              <style>{ORDER_CSS}</style>
              <ThemedBanner theme={getTheme(lookForm.theme)} bannerUrl={look.banner_url ? `${API}/${look.banner_url}` : null} logoUrl={look.logo_url ? `${API}/${look.logo_url}` : null} initials={(businessName || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()} />
              <div style={{ padding: "42px 16px 0" }}>
                <div className="op-name" style={{ fontSize: 24 }}>{businessName}</div>
                {lookForm.tagline && <p className="op-tag">{lookForm.tagline}</p>}
                <div className="op-chips"><span className="op-chip ok">Open now</span><span className="op-chip">Pickup in about {f.prep_minutes} min</span></div>
                <button className="op-btn" style={{ marginTop: 8 }} type="button">View order · $12.00</button>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {tab === "pay" && (
      <div>
      <div className="oo-two" style={{ marginTop: space[3] }}>
      <div>
      <div style={{ ...card, marginTop: 0 }}>
        <div style={styles.title}>How customers pay</div>
        <p style={styles.hint}>Customers don't choose. Your setting decides. Alvna controls which methods are available to you.</p>
        <label style={{ ...styles.choice, opacity: s.allowed.store ? 1 : 0.5 }}>
          <input type="radio" name="pm" disabled={!s.allowed.store} checked={f.payment_mode === "store"} onChange={() => setF({ ...f, payment_mode: "store" })} />
          <span><strong>Pay at the store</strong><br /><span style={styles.hint}>The order waits under “Pending payment”. You call the customer, take payment, then it goes to the kitchen.</span></span>
        </label>
        {f.payment_mode === "store" && (
          <div style={{ marginLeft: 28, marginBottom: space[2] }}>
            <div style={styles.hint}>Orders up to this amount may be paid at pickup (you can send them to the kitchen straight away). Larger orders must be paid by phone first.</div>
            <div style={{ display: "flex", gap: space[3], alignItems: "center", flexWrap: "wrap" }}>
              <label style={styles.small}>Limit $ <input style={{ ...inputBase, width: 100 }} type="number" min="10" max="1000" disabled={f.store_limit_none} value={f.store_limit} onChange={(e) => setF({ ...f, store_limit: e.target.value })} aria-label="Pay-at-store limit" /></label>
              <label style={styles.small}><input type="checkbox" checked={f.store_limit_none} onChange={(e) => setF({ ...f, store_limit_none: e.target.checked })} /> No limit</label>
            </div>
          </div>
        )}
        <label style={{ ...styles.choice, opacity: s.allowed.stripe ? 1 : 0.5 }}>
          <input type="radio" name="pm" disabled={!s.allowed.stripe} checked={f.payment_mode === "stripe"} onChange={() => setF({ ...f, payment_mode: "stripe" })} />
          <span><strong>Card online (Stripe)</strong><br /><span style={styles.hint}>Customers pay by card when they order. Paid orders go to the kitchen automatically.{!s.allowed.stripe && " Not enabled for your account, ask Alvna."}</span></span>
        </label>
        {s.allowed.stripe && (
          <div style={{ ...styles.stripe }} data-testid="stripe-box">
            {st.test && <div style={styles.testBanner}>TEST MODE: no real money moves. Use Stripe's test cards.</div>}
            {!st.keys_configured ? <div style={styles.hint}>Card payments aren't set up on the server yet. Please contact Alvna.</div> : (
              <>
                <div style={{ fontSize: 14 }}>
                  Stripe: <strong style={{ color: st.ready ? "#067647" : "#93370d" }}>{st.ready ? "Connected, ready for payments" : st.connected ? "Setup not finished" : "Not connected"}</strong>
                </div>
                <div style={{ display: "flex", gap: space[2], flexWrap: "wrap", marginTop: space[2] }}>
                  {!st.ready && <button style={buttonPrimary} onClick={() => stripeAction("stripe_connect")} data-testid="stripe-connect">{st.connected ? "Continue Stripe setup" : "Connect Stripe"}</button>}
                  {st.connected && <button style={buttonSecondary} onClick={() => stripeAction("stripe_refresh")}>Refresh status</button>}
                </div>
                <div style={styles.hint}>Payments go straight into your own Stripe account. You manage payouts, refunds and receipts in your Stripe dashboard.</div>
              </>
            )}
          </div>
        )}
      </div>

      </div>
      <div>
      <div style={{ ...card, marginTop: 0 }}>
        <div style={styles.title}>Ordering rules</div>
        <div style={styles.grid}>
          <label style={styles.field}>Prep time (min)<input style={inputBase} type="number" min="5" max="120" value={f.prep_minutes} onChange={(e) => setF({ ...f, prep_minutes: e.target.value })} /></label>
          <label style={styles.field}>Minimum order ($)<input style={inputBase} type="number" min="0" max="500" value={f.min_order} onChange={(e) => setF({ ...f, min_order: e.target.value })} /></label>
          <label style={styles.field}>Hold unpaid orders (min)<input style={inputBase} type="number" min="15" max="120" value={f.hold_minutes} onChange={(e) => setF({ ...f, hold_minutes: e.target.value })} /></label>
          <label style={styles.field}>Orders per 15-min slot (0 = no limit)<input style={inputBase} type="number" min="0" max="50" value={f.slot_max_orders} onChange={(e) => setF({ ...f, slot_max_orders: e.target.value })} /></label>
          <label style={styles.field}>Schedule up to (days ahead)<input style={inputBase} type="number" min="1" max="14" value={f.max_days_ahead} onChange={(e) => setF({ ...f, max_days_ahead: e.target.value })} /></label>
        </div>
        <div style={{ display: "flex", gap: space[4], flexWrap: "wrap", margin: `${space[2]} 0` }}>
          <label style={styles.small}><input type="checkbox" checked={f.asap_enabled} onChange={(e) => setF({ ...f, asap_enabled: e.target.checked })} /> Allow ASAP pickup</label>
          <label style={styles.small}><input type="checkbox" checked={f.scheduled_enabled} onChange={(e) => setF({ ...f, scheduled_enabled: e.target.checked })} /> Allow scheduled pickup</label>
        </div>
        <label style={styles.field}>Pickup notes shown to customers (optional)
          <textarea style={{ ...inputBase, minHeight: 60 }} maxLength={500} value={f.pickup_notes} onChange={(e) => setF({ ...f, pickup_notes: e.target.value })} placeholder="e.g. Ring the bell at the side door" />
        </label>
      </div>

      </div>
      </div>
      <div style={{ marginTop: space[3] }}>
      <div style={{ ...card, marginTop: 0 }}>
        <div style={styles.title}>Online ordering hours <span style={styles.hint}>({s.timezone})</span></div>
        <div className="oo-hours">
        {DAYS.map(([k, label]) => (
          <div key={k} style={styles.dayRow}>
            <span style={{ width: 90 }}>{label}</span>
            <label style={styles.small}><input type="checkbox" checked={!!f.hours[k].closed} onChange={(e) => setDay(k, { closed: e.target.checked })} /> Closed</label>
            {!f.hours[k].closed && (
              <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input type="time" style={{ ...inputBase, width: 120 }} value={f.hours[k].open} onChange={(e) => setDay(k, { open: e.target.value })} aria-label={`${label} opens`} />–
                <input type="time" style={{ ...inputBase, width: 120 }} value={f.hours[k].close} onChange={(e) => setDay(k, { close: e.target.value })} aria-label={`${label} closes`} />
              </span>
            )}
          </div>
        ))}
        </div>
      </div>

      <div style={{ marginTop: space[3] }}><button style={buttonPrimary} onClick={save} data-testid="save-online">Save settings</button></div>
      </div>
      </div>
      )}

      {tab === "orders" && (
      <div style={{ ...card, marginTop: space[3] }}>
        <div style={styles.title}>Recent online orders</div>
        <div style={{ display: "flex", gap: space[2], margin: `${space[2]} 0`, flexWrap: "wrap", alignItems: "center" }}>
          <input style={{ ...inputBase, flex: "1 1 200px", minWidth: 0 }} placeholder="Search by order number, phone or name" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setHistoryPage(1), loadHistory(q, 1))} aria-label="Search orders" />
          <button style={buttonSecondary} onClick={() => { setHistoryPage(1); loadHistory(q, 1); }}>Search</button>
          <label style={{ display: "flex", alignItems: "center", gap: space[1], fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary, whiteSpace: "nowrap" }}>
            Rows per page:
            <select value={historyPageSize} onChange={(e) => { setHistoryPageSize(parseInt(e.target.value)); setHistoryPage(1); loadHistory(q, 1); }} style={{ ...inputBase, width: 70, padding: "6px 8px" }}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
        {history.length === 0 ? (
          <p style={styles.hint}>No orders found.</p>
        ) : (
          <>
            {history.map((o) => (
              <div key={o.id} style={styles.hist} data-testid="history-row">
                <strong>{o.order_number}</strong><span>{o.customer_name} · {o.phone_display}</span><span>{o.status.replace("_", " ")}</span><span>{money(o.total)}</span><span style={{ color: colors.textMuted }}>{o.placed_local}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: space[3], fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary }}>
              <span>Showing {Math.min((historyPage - 1) * historyPageSize + 1, historyTotalCount)}–{Math.min(historyPage * historyPageSize, historyTotalCount)} of {historyTotalCount}</span>
              <div style={{ display: "flex", gap: space[2] }}>
                <button onClick={() => { const newPage = Math.max(1, historyPage - 1); setHistoryPage(newPage); loadHistory(q, newPage); }} disabled={historyPage === 1} style={{ ...buttonSecondary, opacity: historyPage === 1 ? 0.5 : 1, fontSize: 12 }}>Previous</button>
                <button onClick={() => { const newPage = Math.min(historyTotalPages, historyPage + 1); setHistoryPage(newPage); loadHistory(q, newPage); }} disabled={historyPage >= historyTotalPages} style={{ ...buttonSecondary, opacity: historyPage >= historyTotalPages ? 0.5 : 1, fontSize: 12 }}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {showQr && <QrSheetModal businessName={businessName} title="Print online-ordering QR code" intro="One code that opens your online menu. Put it on tables, the door or a flyer." subtitle="Order online for pickup" instructions="Scan with your phone camera to order." links={[{ key: "order", label: "Order online", url: s.order_url }]} onClose={() => setShowQr(false)} />}
    </>
  );
}

const styles = {
  row: { display: "flex", justifyContent: "space-between", gap: space[3], alignItems: "center", flexWrap: "wrap" },
  title: { fontFamily: fonts.display, fontWeight: 600, fontSize: 16, color: colors.textPrimary },
  hint: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, margin: `${space[1]} 0` },
  msg: { fontFamily: fonts.body, fontSize: 14, borderRadius: radius.md, padding: space[3], marginTop: space[3] },
  choice: { display: "flex", gap: 10, alignItems: "flex-start", fontFamily: fonts.body, fontSize: 14, padding: `${space[2]} 0`, cursor: "pointer" },
  small: { fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary, display: "flex", alignItems: "center", gap: 6 },
  stripe: { marginLeft: 28, background: colors.surfaceMuted, borderRadius: radius.md, padding: space[3], fontFamily: fonts.body },
  testBanner: { background: "#fffaeb", color: "#93370d", borderRadius: radius.sm, padding: "6px 10px", fontSize: 12, fontWeight: 600, marginBottom: space[2] },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: space[3], marginTop: space[2] },
  field: { display: "flex", flexDirection: "column", gap: 4, fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary },
  dayRow: { display: "flex", gap: space[3], alignItems: "center", flexWrap: "wrap", padding: `${space[1]} 0`, fontFamily: fonts.body, fontSize: 14 },
  themeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, marginTop: 8 },
  themeTile: { border: "1px solid", borderRadius: 10, padding: 8, cursor: "pointer", textAlign: "left" },
  removeLink: { background: "none", border: "none", color: "#b91c1c", cursor: "pointer", fontSize: 12, marginLeft: 10 },
  hist: { display: "flex", gap: space[3], flexWrap: "wrap", justifyContent: "space-between", padding: `${space[2]} 0`, borderTop: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 13 },
};
